/**
 * テキストの行数を自動制限する機能
 *
 * 仕様
 * - ist-card04__contentInner: 親要素
 * - ist-card04__category: カテゴリー
 * - ist-card04__title: タイトル親要素
 * - ist-card04__titleText: タイトル
 * - ist-card04__main: 本文親要素
 * - ist-card04__mainText: 本文
 *
 * 使い方
 * <div class="ist-card04__contentInner">
 *   <div class="ist-card04__category">カテゴリー</div>
 *   <div class="ist-card04__title">
 *     <div class="ist-card04__titleText">タイトル</div>
 *   </div>
 *   <div class="ist-card04__main">
 *     <div class="ist-card04__mainText">本文</div>
 *   </div>
 * </div>
 */

export class Card04LineClamp {
  private static readonly CLASSES = {
    CONTENT_INNER: 'ist-card04__contentInner',
    CATEGORY: 'ist-card04__category',
    TITLE_WRAPPER: 'ist-card04__title',
    TITLE_TEXT: 'ist-card04__titleText',
    MAIN_WRAPPER: 'ist-card04__main',
    MAIN_TEXT: 'ist-card04__mainText',
  };

  private resizeObserver: ResizeObserver;
  private resizeTimeout: number | null = null;
  private readonly NO_DISPLAY_CLASS = 'ist-js_card04TextNodisplay';
  private debug: boolean;

  /**
   * Card04LineClampクラスのコンストラクタ
   * @param options デバッグモードを有効にするためのオプション
   */
  constructor(options: { debug?: boolean } = {}) {
    this.debug = options.debug || false;
    this.resizeObserver = new ResizeObserver(this.handleResize.bind(this));

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initialize());
    } else {
      this.initialize();
    }
  }

  /**
   * 初期化処理を行う
   * ResizeObserverの設定と初回の行数制限計算を実行
   */
  private initialize(): void {
    const contentInners = Array.from(
      document.getElementsByClassName(Card04LineClamp.CLASSES.CONTENT_INNER)
    ) as HTMLElement[];

    contentInners.forEach((contentInner) => {
      this.resizeObserver.observe(contentInner);
    });

    this.updateAllLineClamps();
  }

  /**
   * 要素の実際の高さを取得する
   * @param element 高さを測定する要素
   * @returns 要素の実際の高さ（ピクセル）
   */
  private getActualHeight(element: HTMLElement): number {
    const clone = element.cloneNode(true) as HTMLElement;
    const parent = element.parentElement;

    if (!parent) return 0;

    clone.style.cssText = `
      position: absolute !important;
      visibility: hidden !important;
      display: block !important;
      width: ${element.offsetWidth}px !important;
      height: auto !important;
      margin: 0 !important;
      padding: ${window.getComputedStyle(element).padding} !important;
    `;

    parent.appendChild(clone);
    const height = clone.scrollHeight;
    parent.removeChild(clone);

    this.log('Actual height measurement:', {
      element: element.className,
      height,
      content: element.textContent?.slice(0, 50) + '...',
    });

    return height;
  }

  /**
   * 要素の行の高さを取得する
   * @param element 行の高さを取得する要素
   * @returns 行の高さ（ピクセル）
   */
  private getLineHeight(element: HTMLElement): number {
    const style = window.getComputedStyle(element);
    let lineHeight = parseFloat(style.lineHeight);

    if (isNaN(lineHeight)) {
      const fontSize = parseFloat(style.fontSize);
      lineHeight = fontSize * 1.2;
    }

    this.log('Line height:', lineHeight);
    return lineHeight;
  }

  /**
   * 要素の合計の高さを取得する
   * @param element 高さを取得する要素
   * @returns 要素の合計の高さ（ピクセル）
   */
  private getTotalElementHeight(element: HTMLElement): number {
    const height = element.offsetHeight;
    this.log('Element height:', { element: element.className, height });
    return height;
  }

  /**
   * 要素のスタイルをリセットする
   * @param contentInner リセット対象の親要素
   */
  private resetStyles(contentInner: HTMLElement): void {
    const titleText = contentInner.querySelector(
      `.${Card04LineClamp.CLASSES.TITLE_TEXT}`
    ) as HTMLElement;
    const mainText = contentInner.querySelector(
      `.${Card04LineClamp.CLASSES.MAIN_TEXT}`
    ) as HTMLElement;
    const mainWrapper = contentInner.querySelector(
      `.${Card04LineClamp.CLASSES.MAIN_WRAPPER}`
    ) as HTMLElement;

    if (titleText) {
      titleText.style.removeProperty('--line-clamp');
      titleText.classList.remove(this.NO_DISPLAY_CLASS);
    }
    if (mainText) {
      mainText.style.removeProperty('--line-clamp');
    }
    if (mainWrapper) {
      mainWrapper.classList.remove(this.NO_DISPLAY_CLASS);
    }
  }

  /**
   * テキストの行数を計算して制限する
   * @param contentInner 行数制限を適用する親要素
   */
  private calculateLineClamp(contentInner: HTMLElement): void {
    const contentInnerHeight = contentInner.offsetHeight;

    // カテゴリーの高さを計算
    const category = contentInner.querySelector(
      `.${Card04LineClamp.CLASSES.CATEGORY}`
    ) as HTMLElement;
    const categoryHeight = category ? this.getTotalElementHeight(category) : 0;

    // タイトル関連の計算
    const titleWrapper = contentInner.querySelector(
      `.${Card04LineClamp.CLASSES.TITLE_WRAPPER}`
    ) as HTMLElement;
    if (!titleWrapper) return;

    const titleWrapperStyle = window.getComputedStyle(titleWrapper);
    const titleWrapperTopSpace =
      parseFloat(titleWrapperStyle.marginTop) +
      parseFloat(titleWrapperStyle.paddingTop);

    const titleText = titleWrapper.querySelector(
      `.${Card04LineClamp.CLASSES.TITLE_TEXT}`
    ) as HTMLElement;
    if (!titleText) return;

    const titleTextLineHeight = this.getLineHeight(titleText);
    const actualTitleHeight = this.getActualHeight(titleText);
    const availableTitleHeight =
      contentInnerHeight - categoryHeight - titleWrapperTopSpace;

    // タイトルが利用可能な高さを超える場合
    if (actualTitleHeight > availableTitleHeight) {
      const titleMaxLines = Math.floor(
        availableTitleHeight / titleTextLineHeight
      );
      if (titleMaxLines <= 0) {
        titleText.classList.add(this.NO_DISPLAY_CLASS);
      } else {
        titleText.style.setProperty('--line-clamp', titleMaxLines.toString());
      }

      const mainWrapper = contentInner.querySelector(
        `.${Card04LineClamp.CLASSES.MAIN_WRAPPER}`
      ) as HTMLElement;
      if (mainWrapper) {
        mainWrapper.classList.add(this.NO_DISPLAY_CLASS);
      }
      return;
    }

    // 本文関連の計算
    const mainWrapper = contentInner.querySelector(
      `.${Card04LineClamp.CLASSES.MAIN_WRAPPER}`
    ) as HTMLElement;
    if (!mainWrapper) return;

    const mainWrapperStyle = window.getComputedStyle(mainWrapper);
    const mainWrapperTopSpace =
      parseFloat(mainWrapperStyle.marginTop) +
      parseFloat(mainWrapperStyle.paddingTop);

    const mainText = mainWrapper.querySelector(
      `.${Card04LineClamp.CLASSES.MAIN_TEXT}`
    ) as HTMLElement;
    if (!mainText) return;

    const availableMainHeight =
      contentInnerHeight -
      categoryHeight -
      titleWrapperTopSpace -
      actualTitleHeight -
      mainWrapperTopSpace;
    const actualMainHeight = this.getActualHeight(mainText);
    const mainTextLineHeight = this.getLineHeight(mainText);

    // 本文の表示制御
    if (availableMainHeight <= 0) {
      mainWrapper.classList.add(this.NO_DISPLAY_CLASS);
    } else if (actualMainHeight > availableMainHeight) {
      const mainMaxLines = Math.floor(availableMainHeight / mainTextLineHeight);
      if (mainMaxLines <= 0) {
        mainWrapper.classList.add(this.NO_DISPLAY_CLASS);
      } else {
        mainText.style.setProperty('--line-clamp', mainMaxLines.toString());
        mainWrapper.classList.remove(this.NO_DISPLAY_CLASS);
      }
    } else {
      mainWrapper.classList.remove(this.NO_DISPLAY_CLASS);
    }

    this.log('Space calculation:', {
      contentInnerHeight,
      categoryHeight,
      titleWrapperTopSpace,
      actualTitleHeight,
      availableTitleHeight,
      mainWrapperTopSpace,
      availableMainHeight,
      actualMainHeight,
    });
  }

  /**
   * リサイズイベントのハンドラー
   * @param entries リサイズされた要素のエントリー配列
   */
  private handleResize(entries: ResizeObserverEntry[]): void {
    if (this.resizeTimeout) {
      window.cancelAnimationFrame(this.resizeTimeout);
    }

    this.resizeTimeout = window.requestAnimationFrame(() => {
      entries.forEach((entry) => {
        const contentInner = entry.target as HTMLElement;
        this.resetStyles(contentInner);
        this.calculateLineClamp(contentInner);
      });
      this.resizeTimeout = null;
    });
  }

  /**
   * すべての要素の行数制限を更新する
   */
  private updateAllLineClamps(): void {
    const contentInners = Array.from(
      document.getElementsByClassName(Card04LineClamp.CLASSES.CONTENT_INNER)
    ) as HTMLElement[];

    contentInners.forEach((contentInner) => {
      this.resetStyles(contentInner);
      this.calculateLineClamp(contentInner);
    });
  }

  /**
   * デバッグログを出力する
   * @param args ログ出力する引数
   */
  private log(...args: any[]): void {
    if (this.debug) {
      console.log('[LineClamp]', ...args);
    }
  }

  /**
   * インスタンスを破棄する
   * ResizeObserverの監視を解除し、タイムアウトをクリアする
   */
  public destroy(): void {
    if (this.resizeTimeout) {
      window.cancelAnimationFrame(this.resizeTimeout);
    }
    this.resizeObserver.disconnect();
  }

  /**
   * 行数制限の再計算を実行する
   */
  public recalculate(): void {
    this.updateAllLineClamps();
  }
}
